import {
    Directive,
    Injector,
    Input,
    EventEmitter,
    ElementRef,
    Renderer2,
    TemplateRef,
    ViewContainerRef,
    ComponentRef,
    OnInit,
    Output,
    OnDestroy
} from '@angular/core';

import { Subscription, BehaviorSubject, timer } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { without, contains } from 'underscore';

import { WatchedElementModel } from '../models/watched-element.model';
import { CoordinatesModel } from '../models/coordinates.model';
import { directionsEnum } from '../enums/directions.enum';
import { CoordinatesHelper } from '../helpers/coordinates/coordinates.helper';
import { PopoverComponent } from '../components/popover/popover.component';
import { WatchCoordinatesService } from '../services/watch-coordinates.service';
import { PopoversService } from '../services/popovers.service';
import { axesEnum } from '../enums/axes.enum';
import { scalingTypesEnum } from '../enums/scaling-types.enum';
import { dimensionsEnum } from '../enums/dimensions.enum';

@Directive({
    selector: '[agprPopover]',
    exportAs: 'agprPopover',
    providers: [WatchCoordinatesService],
    standalone: false
})
export class PopoverDirective implements OnInit, OnDestroy {

    @Input('agprPopover') popoverContentTemplate;
    /* eslint-disable @angular-eslint/no-input-rename */
    @Input('popoverContainer') customContainer: ElementRef = null;
    @Input('popoverOffsetTop') offsetTop: number = 0;
    @Input('popoverOpenDelay') openDelay: number = 250;
    @Input('popoverCloseDelay') closeDelay: number = 450;
    @Input('popoverZIndex') zIndex: number | string = 'auto';
    @Input('popoverStyleClass') styleClass: string;
    @Input('popoverPositions') preferredPositions: directionsEnum[] = [
        directionsEnum.top,
        directionsEnum.right,
        directionsEnum.bottom,
        directionsEnum.left
    ];

    // Accessibility
    @Input('popoverText') set textContent(text: string) {
        this.el.nativeElement.setAttribute('aria-label', text);
    }
    @Input('initiallyFocusable') initiallyFocusable: boolean = true;

    // Opening events
    @Output() openingPopover: EventEmitter<void> = new EventEmitter<void>();
    @Output() openedPopover: EventEmitter<void> = new EventEmitter<void>();

    // Closing events
    @Output() closingPopover: EventEmitter<void> = new EventEmitter<void>();
    @Output() closedPopover: EventEmitter<void> = new EventEmitter<void>();

    private popover: ComponentRef<PopoverComponent>;
    private body: HTMLElement = null;
    private triggerEl: HTMLElement = this.el.nativeElement;
    private popoverEl: HTMLElement = null;
    private watchedEls: WatchedElementModel[] = [];
    private arrowOffset: number = 11;
    private exitPopover: BehaviorSubject<void> = null;
    private openCountdown: NodeJS.Timeout = null;
    private closeCountdown: NodeJS.Timeout = null;
    private containerClass: string = 'agpr-popoverContext';

    private subscriptions: Subscription[] = [];

    /**
     * Determines whether or not the popover has been created
    */
    private get hasCreatedPopover(): boolean {
        const hasCreatedPopover: boolean = this.popover instanceof ComponentRef;

        return hasCreatedPopover;
    }

    /**
     * Determines whether or not a custom container has been set
    */
    private get hasCustomContainer(): boolean {
        const hasCustomContainer: boolean = this.customContainer instanceof ElementRef;

        return hasCustomContainer;
    }

    /**
     * Determines whether or not the container is the body
    */
    private get isContainerBody(): boolean {
        const container: HTMLElement = this.popoverContainer;
        const isContainerBody: boolean = container === this.body;

        return isContainerBody;
    }

    /**
     * Determines whether or not the popover is already inside its container
    */
    private get isPopoverInContainer(): boolean {
        const container: HTMLElement = this.popoverContainer;
        const containerChildren: NodeList = container.childNodes;
        const isPopoverInContainer: boolean = contains(containerChildren, this.popoverEl);

        return isPopoverInContainer;
    }

    /**
     * Gets the container the popover should operate within
    */
    private get popoverContainer(): HTMLElement {
        const hasCustomContainer: boolean = this.hasCustomContainer;
        let popoverContext: HTMLElement;

        if (hasCustomContainer) {
            popoverContext = this.customContainer.nativeElement;
        } else {
            popoverContext = this.body;
        }

        return popoverContext;
    }

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private viewContainerRef: ViewContainerRef,
        private injector: Injector,
        private watchCoordinatesService: WatchCoordinatesService,
        private popoversService: PopoversService
    ) {}

    ngOnInit() {
        this.createProviders();
        this.createSubscriptions();
        this.getBody();
        this.listen();

        this.el.nativeElement.setAttribute('tabindex', this.initiallyFocusable ? '0' : '-1');
    }

    ngOnDestroy() {
        this.removePopover();
        this.destroySubscriptions();
        this.ignoreElementCoordinates();
    }

    /**
     * Listen to DOM events on the trigger
    */
    private listen() {
        this.renderer
            .listen(this.triggerEl, 'mouseenter', () => {
                this.open();
            });
        this.renderer
            .listen(this.triggerEl, 'focus', () => {
                this.open();
            });

        this.renderer
            .listen(this.triggerEl, 'mouseleave', () => {
                this.cancelOpenCountdown();
            });
        this.renderer
            .listen(this.triggerEl, 'blur', () => {
                this.cancelOpenCountdown();
            });
    }

    /**
     * Create the popover component and insert it into the DOM
    */
    private createPopover(): ComponentRef<PopoverComponent> {
        let popover: ComponentRef<PopoverComponent>;

        if (this.popoverContentTemplate instanceof TemplateRef) {

            popover = this.viewContainerRef.createComponent(
                PopoverComponent,
                {
                    index: 0,
                    injector: this.injector
                }
            );

            popover.instance.contentTemplate = this.popoverContentTemplate;
            if (this.styleClass) {
                popover.instance.styleClass = this.styleClass;
            }

            this.popoverEl = popover.location.nativeElement.firstElementChild;
            this.popover = popover;

            this.appendPopover();
        }

        return popover;
    }

    /**
     * Appends the popover to the parent element
    */
    private appendPopover(): void {
        const hasCreatedPopover: boolean = this.hasCreatedPopover;
        const container: HTMLElement = this.popoverContainer;

        if (hasCreatedPopover) {
            const popover: ComponentRef<PopoverComponent> = this.popover;
            const isPopoverInContainer: boolean = this.isPopoverInContainer;

            if (!isPopoverInContainer) {

                container.appendChild(this.popoverEl);

                popover.changeDetectorRef.markForCheck();
            }
        }
    }

    /**
     * Removes the popover from the parent element
    */
    private removePopover(): void {
        const hasCreatedPopover: boolean = this.hasCreatedPopover;
        const container: HTMLElement = this.popoverContainer;

        if (hasCreatedPopover) {
            const isPopoverInContainer: boolean = this.isPopoverInContainer;

            if (isPopoverInContainer) {
                container.removeChild(this.popoverEl);
            }
        }
    }

    /**
     * Open the popover
    */
    private open(): void {
        let popoverExists = this.popover instanceof ComponentRef;

        this.openingPopover.emit();

        // If this is the first time,
        // create the popover before opening it
        if (!popoverExists) {
            const popover = this.createPopover();

            popoverExists = popover instanceof ComponentRef;

            this.detectExitPopover();
            this.detectEnterPopover();
        }

        // Now that the popover exists, open it
        if (popoverExists) {
            this.popoversService.setOpenPopover(this.popover);
            this.appendPopover();

            this.startOpenCountdown();
        }
    }

    /**
     * Close the popover
    */
    private close() {
        this.closingPopover.emit();

        this.popoverEl.style.visibility = 'hidden';
        this.popoverEl.style.opacity = '0';

        this.resetPositioning();
        this.ignoreElementCoordinates();
        this.removePopover();
        this.closedPopover.emit();
    }

    /**
     * Set the popover position by cycling through all of the
     * preferred directions in order and finding the one that will work best
    */
    private setPosition(
        attemptedPositions: directionsEnum[] = []
    ): void {
        const hasPreferredPositions: boolean = Array.isArray(this.preferredPositions) && this.preferredPositions.length > 0;
        let forcePositioning: boolean;
        let targetPosition: directionsEnum;

        if (!hasPreferredPositions) {
            console.warn('You must specify some preferred popover directions!');

            return;
        }

        // Identify the preferred positions that have not already been attempted
        const targetPositions: directionsEnum[] = without(this.preferredPositions, ...attemptedPositions);
        const hasTargetPositions: boolean = targetPositions.length > 0;

        if (hasTargetPositions) {
            // If there are any positions left to try, try the next one
            targetPosition = targetPositions[0];
        } else {
            // If there are no positions left to try, then none of
            // them could fit the popover without it being cut off.
            // None of them are ideal, so just use the first preferred option.
            targetPosition = this.preferredPositions[0];
            forcePositioning = true;
        }

        // Determine if this attempt was successful or not
        const isPositioningSuccessful: boolean = this.attemptPosition(targetPosition, forcePositioning);

        // If this attempt wasn't successful and there are
        //  more to try, record that this one didn't work and try the next one
        if (!isPositioningSuccessful) {
            attemptedPositions.push(targetPosition);

            this.setPosition(attemptedPositions);
        }
    }

    /**
     * Simulates positioning the popover in the provided direction.
     * If the simulation is successful or the attempt is forced,
     * position the popover in the page
    */
    private attemptPosition(
        direction: directionsEnum,
        forcePositioning: boolean = false
    ): boolean {
        const primaryAxis: axesEnum = this.getDirectionAxis(direction);
        const secondaryAxis: axesEnum = this.getPerpendicularAxis(primaryAxis);
        const primarySpaceRequired: number = this.getPopupSpaceRequired(primaryAxis, direction);
        const secondarySpaceRequired: number = this.getPopupSpaceRequired(secondaryAxis, direction);
        const secondaryDirections: directionsEnum[] = this.getSecondaryDirections(direction);
        const hasRoomPrimary: boolean = this.willFitInDirection(direction, primaryAxis, primarySpaceRequired);
        const hasRoomSecondaryA: boolean = this.willFitInDirection(secondaryDirections[0], primaryAxis, secondarySpaceRequired);
        const hasRoomSecondaryB: boolean = this.willFitInDirection(secondaryDirections[1], primaryAxis, secondarySpaceRequired);
        const isSuccessful: boolean = forcePositioning || (hasRoomPrimary && hasRoomSecondaryA && hasRoomSecondaryB);

        if (isSuccessful || forcePositioning) {
            this.resetPositioning();
            this.positionWithinContainer(direction);
        }

        return isSuccessful;
    }

    /**
     * Get the directions corresponding to the axis
     * that runs perpendicular to the provided direction
    */
    private getSecondaryDirections(
        direction: directionsEnum
    ): directionsEnum[] {
        const directionAxis: axesEnum = this.getDirectionAxis(direction);
        const isVerticalPosition: boolean = directionAxis === axesEnum.y;
        const isHorizontalPosition: boolean = directionAxis === axesEnum.x;
        const secondaryDirections: directionsEnum[] = [];

        if (isVerticalPosition) {
            secondaryDirections.push(
                directionsEnum.right,
                directionsEnum.left
            );
        } else if (isHorizontalPosition) {
            secondaryDirections.push(
                directionsEnum.top,
                directionsEnum.bottom
            );
        }

        return secondaryDirections;
    }

    /**
     * Determines whether or not the popover
     * will fit in the provided direction
    */
    private willFitInDirection(
        direction: directionsEnum,
        primaryAxis: axesEnum,
        spaceRequired: number,
    ): boolean {
        const windowCoordinates: CoordinatesModel = CoordinatesHelper.getWindowCoordinates();
        const willFitInWindow: boolean = this.willFitInCoordinates(direction, primaryAxis, spaceRequired, windowCoordinates);
        const isContainerBody: boolean = this.isContainerBody;
        let willFitInDirection: boolean;

        if (isContainerBody) {
            willFitInDirection = willFitInWindow;
        } else {
            const container: HTMLElement = this.popoverContainer;
            const containerCoordinates: CoordinatesModel = CoordinatesHelper.getElementCoordinates(container);
            const willFitInContainer: boolean = this.willFitInCoordinates(direction, primaryAxis, spaceRequired, containerCoordinates);

            willFitInDirection = willFitInContainer;
        }

        return willFitInDirection;
    }

    /**
     * Determines whether or not the popover will fit in
     * the provided coordinates, based on the provided
     * direction and space required
    */
    private willFitInCoordinates(
        direction: directionsEnum,
        primaryAxis: axesEnum,
        spaceRequired: number,
        coordinates: CoordinatesModel
    ): boolean {
        const triggerCoordinates: CoordinatesModel = CoordinatesHelper.getElementCoordinates(this.triggerEl);

        // Determine whether highers or lower numbers
        // indicate that the popover will fit
        const directionScaling: scalingTypesEnum = this.getDirectionScaling(direction);
        const isAscending: boolean = directionScaling === scalingTypesEnum.ascending;
        const isDescending: boolean = directionScaling === scalingTypesEnum.descending;

        // Get the outer edge of the popover
        // and the edge of the container
        const containerEdgePosition: number = coordinates[direction] + this.getOffsetByDirection(direction);

        const popoverEdgePosition: number = this.getOuterPopoverEdge(
            direction,
            primaryAxis,
            directionScaling,
            triggerCoordinates,
            spaceRequired
        );

        // Determine whether the outer edge of the popover
        // will fit within the inner edge of the targeted parent
        let willFitInCoordinates: boolean;

        if (isAscending) {
            // Based on the provided direction,
            // the outer edge of the popover needs to be a
            // higher number than the inner edge of the container to fit
            willFitInCoordinates = popoverEdgePosition > containerEdgePosition;
        }

        if (isDescending) {
            // Based on the provided direction,
            // the outer edge of the popover needs to be a
            // lower number than the inner edge of the container to fit
            willFitInCoordinates = popoverEdgePosition < containerEdgePosition;
        }

        return willFitInCoordinates;
    }

    /**
     * Gets the position the outer popup edge will need to be set to
     * in order to appear in the right position relative to the trigger
     * within the the popover container (either the container or the body)
    */
    private getOuterPopoverEdge(
        direction: directionsEnum,
        primaryAxis: axesEnum,
        directionScaling: scalingTypesEnum,
        triggerCoordinates: CoordinatesModel,
        spaceRequired: number,
    ): number {
        const isAscendingContext: boolean = directionScaling === scalingTypesEnum.ascending;
        const isDescendingContext: boolean = directionScaling === scalingTypesEnum.descending;
        const directionAxis: axesEnum = this.getDirectionAxis(direction);
        const triggerProportion: number = this.getRequiredProportion(primaryAxis, direction);
        const triggerDimension: dimensionsEnum = this.getAxisDimension(directionAxis);
        const triggerEdge: directionsEnum = this.getOpposingDirection(direction);
        let innerPopoverEdge: number;
        let outerPopoverEdge: number;

        if (isAscendingContext) {
            innerPopoverEdge = triggerCoordinates[triggerEdge] - triggerCoordinates[triggerDimension] * triggerProportion;
            outerPopoverEdge = innerPopoverEdge - spaceRequired;
        }

        if (isDescendingContext) {
            innerPopoverEdge = triggerCoordinates[triggerEdge] + triggerCoordinates[triggerDimension] * triggerProportion;
            outerPopoverEdge = innerPopoverEdge + spaceRequired;
        }

        return outerPopoverEdge;
    }

    /**
     * Determines whether in the given direction elements further from the edge
     * have a higher position number or a lower position number than the edge
    */
    private getDirectionScaling(
        direction: directionsEnum
    ): scalingTypesEnum {
        // Determining whether or not the outer edge of the popup
        // will fit in the provided space in a particular direction
        // depends on whether the outer edge of the popup will fit when
        // it has higher number or a lower number within the grid
        // than the corresponding inner limit (the edge of the targeted parent)

        // Directions where a higher number indicates a further position
        const ascendingDirections: directionsEnum[] = [directionsEnum.top, directionsEnum.left];
        // Directions where a lower number indicates a further position
        const descendingDirections: directionsEnum[] = [directionsEnum.bottom, directionsEnum.right];

        // Determine the scaling type
        const isAscending: boolean = contains(ascendingDirections, direction);
        const isDescending: boolean = contains(descendingDirections, direction);

        // Set the scaling type
        let directionScaling: scalingTypesEnum;

        if (isAscending) {
            directionScaling = scalingTypesEnum.ascending;
        }

        if (isDescending) {
            directionScaling = scalingTypesEnum.descending;
        }

        return directionScaling;
    }

    /**
     * Gets the axis corresponding to the provided dimension
    */
    private getDirectionAxis(
        direction: directionsEnum
    ): axesEnum {
        const verticalDirections: directionsEnum[] = [directionsEnum.top, directionsEnum.bottom];
        const horizontalDirections: directionsEnum[] = [directionsEnum.right, directionsEnum.left];
        const isVertical: boolean = contains(verticalDirections, direction);
        const isHorizontal: boolean = contains(horizontalDirections, direction);
        let axis: axesEnum;

        if (isVertical) {
            axis = axesEnum.y;
        }

        if (isHorizontal) {
            axis = axesEnum.x;
        }

        return axis;
    }

    /**
     * Gets the axis that runs perpendicular
     * to the provided axis
    */
    private getPerpendicularAxis(
        axis: axesEnum
    ): axesEnum {
        let perpendicularAxis: axesEnum;

        switch (axis) {
            case axesEnum.y:
                perpendicularAxis = axesEnum.x;
                break;
            case axesEnum.x:
                perpendicularAxis = axesEnum.y;
                break;
        }

        return perpendicularAxis;
    }

    /**
     * Gets the dimension corresponding to the provided axis
    */
    private getAxisDimension(
        axis: axesEnum
    ): dimensionsEnum {
        const isVertical: boolean = axis === axesEnum.y;
        const isHorizontal: boolean = axis === axesEnum.x;
        let dimension: dimensionsEnum;

        if (isVertical) {
            dimension = dimensionsEnum.height;
        }

        if (isHorizontal) {
            dimension = dimensionsEnum.width;
        }

        return dimension;
    }

    /**
     * Gets the proportion of the trigger or popover
     * required to correctly position the popover
     * relative to a given axis and dimension
    */
    private getRequiredProportion(
        axis: axesEnum,
        direction: directionsEnum
    ): number {
        const directionAxis: axesEnum = this.getDirectionAxis(direction);
        const isVerticalPosition: boolean = directionAxis === axesEnum.y;
        const isHorizontalPosition: boolean = directionAxis === axesEnum.x;
        const full: number = 1;
        const half: number = 0.5;
        let requiredProportion: number;

        if (axis === axesEnum.y) {
            if (isVerticalPosition) {
                requiredProportion = full;
            } else if (isHorizontalPosition) {
                requiredProportion = half;
            }
        } else if (axis === axesEnum.x) {
            if (isHorizontalPosition) {
                requiredProportion = full;
            } else if (isVerticalPosition) {
                requiredProportion = half;
            }
        }

        return requiredProportion;
    }

    /**
     * Determines how much space would be required along the provided axis
     * to position the popup in a given direction relative to the trigger
    */
    private getPopupSpaceRequired(
        axis: axesEnum,
        direction: directionsEnum
    ): number {
        const popoverCoordinates: CoordinatesModel = CoordinatesHelper.getElementCoordinates(this.popoverEl);
        const proportionRequired: number = this.getRequiredProportion(axis, direction);
        const dimension: dimensionsEnum = this.getAxisDimension(axis);
        const totalPopupArea: number = popoverCoordinates[dimension];
        const directionAxis: axesEnum = this.getDirectionAxis(direction);
        const isPrimaryAxis: boolean = axis === directionAxis;
        const arrowOffset: number = this.arrowOffset;
        let spaceRequired: number = totalPopupArea * proportionRequired;

        if (isPrimaryAxis) {
            spaceRequired += arrowOffset;
        }

        return spaceRequired;
    }

    /**
     * Determines what, if any offset should be considered
     * when determining whether or not the popover will fit
     * in a given direction relative to the trigger
    */
    private getOffsetByDirection(
        direction: directionsEnum
    ): number {
        let offset: number = 0;

        if (direction === directionsEnum.top) {
            offset = this.offsetTop;
        }

        return offset;
    }

    /**
     * Resets the positioning of the popover
    */
    private resetPositioning(): void {
        this.popoverEl.style.top = null;
        this.popoverEl.style.right = null;
        this.popoverEl.style.bottom = null;
        this.popoverEl.style.left = null;
    }

    /**
     * Sets the z-index of the popover to
     * the one provided to the directive (or the default)
    */
    private setZIndex(): void {
        this.popoverEl.style.zIndex = this.zIndex.toString();
    }

    /**
     * Positions the popover in the provided direction
     * within either the custom container or the body
    */
    private positionWithinContainer(
        direction: directionsEnum
    ) {
        const container: HTMLElement = this.popoverContainer;
        const triggerCoordinates: CoordinatesModel = CoordinatesHelper.getElementCoordinates(this.triggerEl);
        const containerCoordinates: CoordinatesModel = CoordinatesHelper.getElementCoordinates(container);

        this.positionAxisWithinContainer(direction, axesEnum.y, triggerCoordinates, containerCoordinates);
        this.positionAxisWithinContainer(direction, axesEnum.x, triggerCoordinates, containerCoordinates);

        this.popover.instance.position = direction;
    }

    /**
     * Positions the popover along a single axis
     * relative to the provided edge of its container
    */
    private positionAxisWithinContainer(
        direction: directionsEnum,
        axis: axesEnum,
        triggerCoordinates: CoordinatesModel,
        containerCoordinates: CoordinatesModel
    ): void {
        // Determine whether or not this axis runs parallel to the provided direction
        const directionAxis: axesEnum = this.getDirectionAxis(direction);
        const isParallelAxis: boolean = directionAxis === axis;

        // Find the required coordinates relating to the container
        const containerEdge: directionsEnum = this.getRelativeEdge(direction, axis);
        const containerDimension: dimensionsEnum = this.getRelativeDimension(containerEdge);
        const containerEdgePosition: number = containerCoordinates[containerEdge];

        // Find the required coordinates relating to the trigger
        const triggerEdge: directionsEnum = this.getOpposingDirection(containerEdge);
        const triggerSize: number = triggerCoordinates[containerDimension];
        const triggerEdgePosition: number = triggerCoordinates[triggerEdge];

        // Determine whether highers or lower numbers will
        // indicate a further position from the container edge
        const directionScaling: scalingTypesEnum = this.getDirectionScaling(containerEdge);

        // Get the distance the edge of the popover needs to be
        // from the corresponding edge of the container for this axis
        let distanceFromEdge: number;

        distanceFromEdge = this.getDistanceFromEdgeByAxis(
            triggerEdgePosition,
            containerEdgePosition,
            directionScaling
        );

        if (isParallelAxis) {
            // If the axis runs parallel to the direction the popover
            // is positioned in, add room for the arrow
            distanceFromEdge = distanceFromEdge + this.arrowOffset;
        } else {
            // If the axis runs perpendicular to the direction,
            // position the popover halfway along the trigger to center it
            distanceFromEdge = distanceFromEdge - triggerSize * 0.5;
        }

        // Apply the distance from edge
        // relative to the edge via CSS
        this.applyPopoverAxis(
            containerEdge,
            distanceFromEdge
        );
    }

    /**
     * Positions the popover along a single axis
     * relative to the provided edge of a container
     * using inline CSS styles
    */
    private getDistanceFromEdgeByAxis(
        triggerEdgePosition: number,
        containerEdgePosition: number,
        directionScaling: scalingTypesEnum
    ): number {
        const isContainerBody: boolean = this.isContainerBody;
        const isAscending: boolean = directionScaling === scalingTypesEnum.ascending;
        const isDescending: boolean = directionScaling === scalingTypesEnum.descending;
        let distanceFromEdge: number;

        if (isContainerBody) {
            // If the popover container is the body,
            // calculate the position according to the body

            if (isAscending) {
                distanceFromEdge = triggerEdgePosition;
            }

            if (isDescending) {
                distanceFromEdge = containerEdgePosition - triggerEdgePosition;
            }
        } else {
            // If the container element is not the body,
            // Calculate the position relative to the edge of the container

            if (isAscending) {
                distanceFromEdge = triggerEdgePosition - containerEdgePosition;
            }

            if (isDescending) {
                distanceFromEdge = containerEdgePosition - triggerEdgePosition;
            }
        }

        return distanceFromEdge;
    }

    /**
     * Applies the style necessary to position the popover
     * the provide distance relative to the provided edge of the container
    */
    private applyPopoverAxis(
        edge: string,
        distanceFromEdge: number
    ): void {
        const applicableStyle: string = this.convertToREMs(distanceFromEdge);

        this.popoverEl.style[edge] = applicableStyle;
    }

    /**
     * Gets the edge that should be used to position the popover
     * based on the desired position and the axis currently being set
    */
    private getRelativeEdge(
        direction: directionsEnum,
        axis: axesEnum
    ): directionsEnum {
        const isVerticalDirection: boolean = axis === axesEnum.y;
        const isHorizontalDirection: boolean = axis === axesEnum.x;
        let relativeEdge: directionsEnum;

        if (isVerticalDirection) {
            switch (direction) {
                case directionsEnum.top:
                    relativeEdge = directionsEnum.bottom;
                    break;
                case directionsEnum.right:
                case directionsEnum.bottom:
                case directionsEnum.left:
                    relativeEdge = directionsEnum.top;
                    break;
            }
        }

        if (isHorizontalDirection) {
            switch (direction) {
                case directionsEnum.left:
                    relativeEdge = directionsEnum.right;
                    break;
                case directionsEnum.top:
                case directionsEnum.bottom:
                case directionsEnum.right:
                    relativeEdge = directionsEnum.left;
                    break;
            }
        }

        return relativeEdge;
    }

    /**
     * Gets the edge that sits opposite the provided edge
    */
    private getOpposingDirection(
        edge: directionsEnum
    ): directionsEnum {
        let inverseEdge: directionsEnum;

        switch (edge) {
            case directionsEnum.top:
                inverseEdge = directionsEnum.bottom;
                break;
            case directionsEnum.right:
                inverseEdge = directionsEnum.left;
                break;
            case directionsEnum.bottom:
                inverseEdge = directionsEnum.top;
                break;
            case directionsEnum.left:
                inverseEdge = directionsEnum.right;
                break;
        }

        return inverseEdge;
    }

    /**
     * Gets the dimension that is relative
     * to the axis of the provided direction
    */
    private getRelativeDimension(
        direction: directionsEnum
    ): dimensionsEnum {
        // Get the axis matching the provided position
        const positionAxis: axesEnum = this.getDirectionAxis(direction);

        // Determine if the position is horizontal or vertical
        const isVerticalPosition: boolean = positionAxis === axesEnum.y;
        const isHorizontalPosition: boolean = positionAxis === axesEnum.x;

        // Based on whether the position's axis is horizontal or vertical,
        // Find the corresponding dimension
        let relativeDimension: dimensionsEnum;

        if (isVerticalPosition) {
            relativeDimension = dimensionsEnum.height;
        }

        if (isHorizontalPosition) {
            relativeDimension = dimensionsEnum.width;
        }

        return relativeDimension;
    }

    /**
     * Begins watching the position and size of
     * the popover and its trigger so the popover can be
     * re-rendered if necessary based on changes to the page
    */
    private watchElementCoordinates(): void {
        const watchedTrigger = this.watchCoordinatesService.watchElement(this.triggerEl);
        const watchedPopover = this.watchCoordinatesService.watchElement(this.popoverEl);

        this.watchedEls.push(watchedTrigger);
        this.watchedEls.push(watchedPopover);

        watchedTrigger
            .change
            .pipe(
                debounce(
                    coordinates => timer(100)
                )
            )
            .subscribe(
                coordinates => {
                    this.setPosition();
                }
            );

        watchedPopover
            .resize
            .pipe(
                debounce(
                    coordinates => timer(100)
                )
            )
            .subscribe(
                coordinates => {
                    this.setPosition();
                }
            );

        watchedPopover
            .change
            .pipe(
                debounce(
                    coordinates => timer(100)
                )
            )
            .subscribe(
                coordinates => {
                    this.showPopover();
                }
            );
    }

    /**
     * Stops watching the coordinates of all elements
     * this directive was previously watching
    */
    private ignoreElementCoordinates(): void {
        this.watchedEls.forEach(
            watchedElement => this.watchCoordinatesService.ignoreElement(watchedElement)
        );

        this.watchedEls.length = 0;
    }

    /**
     * Makes the popover visible
    */
    private showPopover(): void {
        this.popoverEl.style.visibility = 'visible';
        this.popoverEl.style.opacity = '1';
    }

    /**
     * Detects the user having moused/focused into the popover
     * or its triggering element
    */
    private detectEnterPopover() {
        if (this.triggerEl) {
            this.renderer.listen(this.triggerEl, 'mouseenter', () => {
                this.cancelCloseCountdown();
            });
            this.renderer.listen(this.triggerEl, 'focus', () => {
                this.cancelCloseCountdown();
            });

        }

        if (this.popoverEl) {
            this.renderer.listen(this.popoverEl, 'mouseenter', () => {
                this.cancelCloseCountdown();
            });
            this.renderer.listen(this.popoverEl, 'focus', () => {
                this.cancelCloseCountdown();
            });
        }
    }

    /**
     * Detects the user having moused/focused away from the popover
     * or its triggering element
    */
    private detectExitPopover() {
        if (this.triggerEl) {
            this.renderer.listen(this.triggerEl, 'mouseleave', () => {
                this.startCloseCountdown();
            });
            this.renderer.listen(this.triggerEl, 'blur', () => {
                this.startCloseCountdown();
            });
        }

        if (this.popoverEl) {
            this.renderer.listen(this.popoverEl, 'mouseleave', () => {
                this.startCloseCountdown();
            });
            this.renderer.listen(this.popoverEl, 'blur', () => {
                this.startCloseCountdown();
            });
        }
    }

    /**
     * Starts the countdown to open the popover
    */
    private startOpenCountdown() {
        this.openCountdown = setTimeout(() => {

            this.prepareContainer();
            this.setZIndex();
            this.setPosition();
            this.watchElementCoordinates();

            this.openedPopover.emit();
        }, this.openDelay);
    }

    /**
     * Prepares the container the popover
     * will appear in to display it properly
    */
    private prepareContainer(): void {
        const containerClass: string = this.containerClass;
        const container: HTMLElement = this.popoverContainer;

        container.classList.add(containerClass);
    }

    /**
     * Cancels the countdown to open the popover
    */
    private cancelOpenCountdown() {
        if (this.openCountdown) {
            clearTimeout(this.openCountdown);

            this.openCountdown = null;
        }
    }

    /**
     * Starts the countdown to close the current popover
    */
    private startCloseCountdown() {
        this.cancelCloseCountdown();

        this.closeCountdown = setTimeout(() => {
            this.exitPopover.next(null);
        }, this.closeDelay);
    }

    /**
     * Cancels the countdown to close the current popover
    */
    private cancelCloseCountdown() {
        if (this.closeCountdown) {
            clearTimeout(this.closeCountdown);

            this.closeCountdown = null;
        }
    }

    /**
     * Converts a raw number into a REM value
    */
    private convertToREMs(rawNumber: number): string {
        const rootFontSize: number = this.getRootFontSize();
        const remOutput: string = rawNumber / rootFontSize + 'rem';

        return remOutput;
    }

    /**
     * Get the root font size of the DOM
    */
    private getRootFontSize(): number {
        const rootElement: HTMLElement = document.documentElement;
        const rootElementStyles: CSSStyleDeclaration = window.getComputedStyle(rootElement);
        const rootFontSizeStyle: string = rootElementStyles.getPropertyValue('font-size');
        const rootFontSize: number = parseFloat(rootFontSizeStyle);

        return rootFontSize;
    }

    /**
     * Gets the body element
    */
    private getBody(): void {
        this.body = document.querySelector('body');
    }

    /**
     * responds to popovers having opened, including other popovers
     * generated by other instances of this directive
    */
    private onOpenPopover(popover: ComponentRef<PopoverComponent>): void {
        // Check that both the new value and the current popover are created popovers
        const hasOpenedPopover: boolean = popover instanceof ComponentRef;
        const hasCreatedThisPopover: boolean = this.popover instanceof ComponentRef;

        // Determines whether or not the popover
        // that just opened is a different popover
        const isThisPopover: boolean =  popover === this.popover;
        const isOpeningAnotherPopover: boolean = hasOpenedPopover && !isThisPopover;

        if (hasCreatedThisPopover && isOpeningAnotherPopover) {
            this.close();
        }
    }

    /**
     * Creates the providers this component relies on
    */
    private createProviders(): void {
        this.exitPopover = new BehaviorSubject<void>(null);
    }

    /**
     * Creates the subscriptions this component relies on
    */
    private createSubscriptions(): void {
        const getPopover: Subscription = this.popoversService
            .getOpenPopover()
            .subscribe(
                popover => this.onOpenPopover(popover)
            );

        const exitPopover: Subscription = this.exitPopover
            .subscribe(
                () => this.onExitPopover()
            );

        this.subscriptions.push(
            getPopover,
            exitPopover
        );
    }

    /**
     * Destroys all of the subscriptions created by this directive
    */
    private destroySubscriptions(): void {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    /**
     * Respond to the user mousing away from
     * the popover and its trigger element
    */
    private onExitPopover(): void {
        const hasCreatedPopover: boolean = this.hasCreatedPopover;

        if (hasCreatedPopover) {
            this.close();
        }
    }

}
